/**
 * Product telemetry — §16.3, ADD-057, BRD-04.
 * Coarse, privacy-safe events only. An explicit opt-out keeps every core
 * feature working; document content, field values, signatures and recipient
 * PII are rejected before they can reach the sink.
 */

export type TelemetryFamily = 'intent' | 'completion' | 'conversion' | 'friction' | 'acquisition'

export interface TelemetryEvent {
  readonly family: TelemetryFamily
  readonly name: string
  readonly properties?: Readonly<Record<string, string | number | boolean>>
}

export interface Telemetry {
  enabled(): boolean
  setEnabled(enabled: boolean): void
  /** Returns true when the event was accepted and forwarded. */
  track(event: TelemetryEvent): boolean
}

const PROHIBITED_PROPERTY =
  /(document|content|field|value|signature|email|phone|recipient|name|token|secret|capability|payload|bytes|address)/i

const MAX_PROPERTY_LENGTH = 64

export interface CreateTelemetryOptions {
  readonly enabled: boolean
  readonly sink: (event: TelemetryEvent) => void
}

export function createTelemetry(options: CreateTelemetryOptions): Telemetry {
  let enabled = options.enabled

  function isSafe(event: TelemetryEvent): boolean {
    if (event.name.length === 0 || event.name.length > MAX_PROPERTY_LENGTH) return false
    for (const [key, value] of Object.entries(event.properties ?? {})) {
      if (PROHIBITED_PROPERTY.test(key)) return false
      if (typeof value === 'string' && value.length > MAX_PROPERTY_LENGTH) return false
    }
    return true
  }

  return {
    enabled: () => enabled,
    setEnabled: (next: boolean) => {
      enabled = next
    },
    track: (event: TelemetryEvent): boolean => {
      if (!enabled) return false
      if (!isSafe(event)) return false
      options.sink(event)
      return true
    },
  }
}
